import './ReferralsLinks.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ActionBottomSheet, Button, Header, Placeholder, ScrollView } from '$uikit';
import { inviteActions, inviteSelector } from '$store/invite';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from '$shared/components';
import { ReferralLinkItem } from '$core/ReferralsLinks/ReferralLinkItem/ReferralLinkItem';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { IcCopy28 } from '$assets';
import copyToClipboard from 'copy-text-to-clipboard';
import { _confirm, createInviteLink } from '$utils';
import { toastActions } from '$store/toast';
import { showModal } from '$navigation/helper';
import { useRoute } from 'react-router5';

export const ReferralsLinks: FC = () => {
  const dispatch = useDispatch();
  const [currentLinkId, setCurrentLinkId] = useState<number>(0);
  const { isStatsLoading, links } = useSelector(inviteSelector);
  const currentUser = useCurrentUserInfo();
  const route = useRoute();
  const t = useTranslator();
  const highlight = route.route.meta?.options.highlight;

  useEffect(() => {
    if (isStatsLoading) {
    }
    dispatch(inviteActions.loadInviteStats());
  }, []);

  const preparedLinks = useMemo(() => {
    return links
      ? [...links].reverse().map((link) => ({
          ...link,
          link: createInviteLink(link.link, currentUser.id, link.id) || link.link,
        }))
      : [];
  }, [links, currentUser.id]);

  const handleCopyLink = useCallback((link) => {
    copyToClipboard(link);
    dispatch(toastActions.setToastSuccess(t('copied')));
  }, []);

  const currentLink = preparedLinks?.find((link) => link.id === currentLinkId);

  const handleActionChange = (value: string) => {
    if (!currentLink) {
      return;
    } else if (value === 'copy') {
      handleCopyLink(currentLink.link);
    } else if (value === 'delete' && currentLink) {
      _confirm(
        t('referral_links_action_delete_confirm', {
          name: currentLink.name,
        }),
        () => dispatch(inviteActions.deleteLinkRequest(currentLink.id)),
      );
    }
    setCurrentLinkId(0);
  };

  return (
    <>
      <Header showCloseButton>{t('referrals_links_title')}</Header>
      <ScrollView className="ReferralsLinks">
        {isStatsLoading ? (
          <Loader isWrapped />
        ) : links?.length ? (
          <div className="ReferralsLinks__links">
            {preparedLinks?.map((link, index) => (
              <ReferralLinkItem
                highlight={highlight && index === 0}
                key={link.id}
                id={link.id}
                userId={currentUser.id}
                name={link.name}
                link={link.link}
                onClick={setCurrentLinkId}
                onCopy={handleCopyLink}
              />
            ))}
          </div>
        ) : (
          <Placeholder
            title={t('referrals_create_link_placeholder_title')}
            actions={
              <Button onClick={() => showModal('create_link')}>
                {t('referrals_create_link_button')}
              </Button>
            }
          >
            {t('referrals_create_link_placeholder_desc')}
          </Placeholder>
        )}
      </ScrollView>
      <ActionBottomSheet
        title={currentLink?.name ?? ''}
        description={currentLink?.link}
        isOpened={!!currentLinkId}
        options={[
          {
            label: t('referral_links_action_copy'),
            value: 'copy',
            icon: <IcCopy28 />,
          },
          {
            label: t('referral_links_action_delete'),
            value: 'delete',
            mode: 'alert',
          },
        ]}
        onChange={handleActionChange}
        onClose={() => setCurrentLinkId(0)}
      />
    </>
  );
};
